/**
 * abc-debug.js
 * ABC Personalization debug panel + console API.
 * Activated by: ?abc_debug=1 in URL  OR  localStorage.abc_debug = '1'
 * Deactivated by: clicking × in panel header
 */
(function () {
  'use strict';

  // ── Activation ─────────────────────────────────────────────────────────────
  const params = new URLSearchParams(location.search);
  if (params.has('abc_debug')) localStorage.setItem('abc_debug', '1');
  if (localStorage.getItem('abc_debug') !== '1') return;

  // ── Constants ──────────────────────────────────────────────────────────────
  const LS = { visits: 'abc_v', products: 'abc_products', cat: 'abc_cat', ref: 'abc_ref', seen: 'abc_seen' };

  const STAGES = {
    Discovery:     { color: '#94a3b8', bg: '#1e293b', step: 1 },
    Consideration: { color: '#60a5fa', bg: '#1e3a5f', step: 2 },
    Conversion:    { color: '#fbbf24', bg: '#422006', step: 3 },
    Subscription:  { color: '#34d399', bg: '#064e3b', step: 4 },
  };

  const PRESETS = {
    'anon-v1':      { customer: { loggedIn: false, firstName: null, ordersCount: null, isClub: false, isSubscriber: false, subscriberDiscount: 0, tier: null }, visits: { count: 1, lastVisit: today(), firstVisit: today() } },
    'anon-v3':      { customer: { loggedIn: false, firstName: null, ordersCount: null, isClub: false, isSubscriber: false, subscriberDiscount: 0, tier: null }, visits: { count: 3, lastVisit: today(), firstVisit: daysAgo(4) }, products: ['4208349642833', '4208349610065'], cat: 'beer' },
    'anon-v5':      { customer: { loggedIn: false, firstName: null, ordersCount: null, isClub: false, isSubscriber: false, subscriberDiscount: 0, tier: null }, visits: { count: 5, lastVisit: today(), firstVisit: daysAgo(10) }, products: ['4208350003281', '6789241831505', '4208349937745'], cat: 'beer' },
    'anon-v7':      { customer: { loggedIn: false, firstName: null, ordersCount: null, isClub: false, isSubscriber: false, subscriberDiscount: 0, tier: null }, visits: { count: 7, lastVisit: today(), firstVisit: daysAgo(20) }, products: ['6942682349649', '4208349642833'], cat: 'beer' },
    'loggedin-0':   { customer: { loggedIn: true, firstName: 'Alex', ordersCount: 0, isClub: false, isSubscriber: false, subscriberDiscount: 0, tier: 'new' }, visits: { count: 3, lastVisit: today(), firstVisit: daysAgo(5) } },
    'loggedin-8':   { customer: { loggedIn: true, firstName: 'Alex', ordersCount: 8, isClub: false, isSubscriber: false, subscriberDiscount: 0, tier: 'growth' }, visits: { count: 4, lastVisit: today(), firstVisit: daysAgo(30) } },
    'club-no-sns':  { customer: { loggedIn: true, firstName: 'Alex', ordersCount: 24, isClub: true, isSubscriber: false, subscriberDiscount: 0, tier: 'ideal' }, visits: { count: 8, lastVisit: today(), firstVisit: daysAgo(60) } },
    'club-sns':     { customer: { loggedIn: true, firstName: 'Alex', ordersCount: 30, isClub: true, isSubscriber: true, subscriberDiscount: 10, tier: 'ideal' }, visits: { count: 12, lastVisit: today(), firstVisit: daysAgo(90) } },
  };

  // ── Helpers ────────────────────────────────────────────────────────────────
  function today() { return new Date().toISOString().slice(0, 10); }

  function daysAgo(n) {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString().slice(0, 10);
  }

  function lsGet(key) { try { return JSON.parse(localStorage.getItem(key)); } catch { return null; } }
  function lsSet(key, v) { localStorage.setItem(key, JSON.stringify(v)); }

  function getBehavioral() {
    const v = lsGet(LS.visits) || {};
    const p = lsGet(LS.products);
    const c = lsGet(LS.cat);
    const r = lsGet(LS.ref);
    const s = lsGet(LS.seen);
    return {
      visitCount:   v.count || 0,
      lastVisit:    v.lastVisit || null,
      firstVisit:   v.firstVisit || null,
      lastProducts: p?.ids || [],
      lastCategory: c?.handle || null,
      refSource:    r?.source || null,
      seenOffers:   s || {},
    };
  }

  // ── Decision logic (preview of abc-personalize.js) ─────────────────────────
  function deriveStage(visitCount) {
    if (!visitCount || visitCount <= 1) return 'Discovery';
    if (visitCount <= 3) return 'Consideration';
    if (visitCount <= 6) return 'Conversion';
    return 'Subscription';
  }

  function deriveOfferFraming(c, b) {
    if (c.loggedIn) {
      if (c.isClub && c.isSubscriber)
        return { match: 'suppress', reason: 'Club + S&S active — no enrollment offers shown' };
      if (c.isClub && !c.isSubscriber)
        return { match: 'sns-enrollment', reason: 'Club member not on S&S → S&S enrollment offer' };
      if (c.ordersCount >= 5 && !c.isClub)
        return { match: 'club-enrollment', reason: `${c.ordersCount} orders, no Club (${c.tier} tier) → Club enrollment + savings calc` };
      if (c.ordersCount >= 1 && !c.isClub)
        return { match: 'sns-intro', reason: `${c.ordersCount} order(s), no Club → S&S intro offer` };
    }
    const stage = deriveStage(b.visitCount);
    const ctaMap = {
      Discovery:     c.loggedIn ? '"Find your brew + join the Club"' : '"Find your brew" · "Shop the lineup"',
      Consideration: '"4.8 stars · 12,000+ reviews" — social proof',
      Conversion:    'Variety pack / sampler bundle — lower the barrier',
      Subscription:  '"Subscribe & Save 10% — skip or cancel anytime"',
    };
    const prefix = (c.loggedIn && c.ordersCount === 0) ? 'Logged in, 0 orders → ' : 'Anonymous → ';
    return { match: `visit-stage-${stage.toLowerCase()}`, reason: `${prefix}${stage} stage`, cta: ctaMap[stage] };
  }

  function deriveProductShelf(c, b) {
    if (c.isClub)
      return { match: 'liquid-club', reason: 'Club member — existing Liquid flow handles shelf' };
    if (c.ordersCount >= 1)
      return { match: 'best-sellers', reason: `Logged in ${c.ordersCount} orders — offer framing only for now` };
    if (!b.visitCount || b.visitCount <= 1)
      return { match: 'best-sellers', reason: 'Visit 1 — no behavioral history yet' };
    if (!window.__abc?.consent?.preferences)
      return { match: 'best-sellers', reason: 'No preferences consent — cannot read behavioral signals' };
    if (b.lastProducts.length > 0)
      return { match: 'ml-seed', reason: `Shopify ML seeded from product #${b.lastProducts[0]}` };
    if (b.lastCategory)
      return { match: 'last-category', reason: `Last browsed collection: "${b.lastCategory}"` };
    return { match: 'best-sellers', reason: 'No behavioral signals — fallback' };
  }

  // ── CSS ────────────────────────────────────────────────────────────────────
  const CSS = `
    #abc-dbg *{box-sizing:border-box;font-family:'Menlo','Monaco','Consolas',monospace;font-size:11px;line-height:1.4;}
    #abc-dbg{position:fixed;bottom:16px;right:16px;width:360px;background:#0f172a;color:#e2e8f0;border:1px solid #1e293b;border-radius:8px;box-shadow:0 12px 40px rgba(0,0,0,.7);z-index:2147483647;}
    #abc-dbg.abc-collapsed #abc-dbg-body{display:none;}
    #abc-dbg-hdr{display:flex;align-items:center;padding:7px 10px;background:#1e293b;border-radius:8px 8px 0 0;cursor:move;gap:6px;}
    #abc-dbg-hdr .abc-t{flex:1;font-weight:700;font-size:11px;color:#7dd3fc;letter-spacing:.08em;}
    #abc-dbg-hdr .abc-t span{color:#475569;font-weight:400;}
    #abc-dbg-hdr button{background:none;border:none;color:#475569;cursor:pointer;padding:1px 5px;border-radius:3px;}
    #abc-dbg-hdr button:hover{color:#e2e8f0;background:#334155;}
    #abc-dbg-body{max-height:72vh;overflow-y:auto;}
    #abc-dbg-body::-webkit-scrollbar{width:3px;}
    #abc-dbg-body::-webkit-scrollbar-thumb{background:#334155;border-radius:2px;}
    .dbg-s{padding:7px 10px;border-bottom:1px solid #1e293b;}
    .dbg-s:last-child{border-bottom:none;}
    .dbg-lbl{color:#334155;font-size:10px;letter-spacing:.1em;text-transform:uppercase;margin-bottom:4px;font-weight:700;}
    .dbg-row{display:flex;align-items:baseline;gap:4px;margin:1px 0;}
    .dbg-k{color:#475569;min-width:88px;flex-shrink:0;}
    .dbg-v{color:#f1f5f9;word-break:break-all;}
    .dbg-v.t{color:#34d399;} .dbg-v.f{color:#f87171;} .dbg-v.n{color:#334155;font-style:italic;}
    .dbg-stage{display:inline-flex;align-items:center;gap:6px;padding:2px 8px;border-radius:4px;font-weight:700;}
    .dbg-dots{display:flex;gap:3px;margin-top:3px;}
    .dbg-dot{height:3px;border-radius:2px;background:#1e293b;}
    .dbg-dot.on{background:var(--sc);}
    .dbg-card{background:#1e293b;border-radius:4px;padding:5px 8px;margin:2px 0;border-left:2px solid #334155;}
    .dbg-card .m{color:#7dd3fc;font-weight:700;} .dbg-card .r{color:#64748b;margin-top:1px;} .dbg-card .c{color:#fbbf24;margin-top:1px;font-style:italic;}
    .dbg-btns{display:flex;flex-wrap:wrap;gap:3px;margin-top:4px;}
    .dbg-btn{background:#1e293b;border:1px solid #334155;color:#94a3b8;padding:2px 7px;border-radius:4px;cursor:pointer;font-family:inherit;font-size:11px;}
    .dbg-btn:hover{background:#334155;color:#e2e8f0;}
    .dbg-btn.red{border-color:#7f1d1d;color:#fca5a5;} .dbg-btn.red:hover{background:#7f1d1d;}
    .dbg-btn.blue{border-color:#1e40af;color:#93c5fd;} .dbg-btn.blue:hover{background:#1e40af;}
    .dbg-btn.green{border-color:#14532d;color:#6ee7b7;} .dbg-btn.green:hover{background:#14532d;}
    .dbg-step{display:flex;align-items:center;gap:4px;}
    .dbg-step .n{color:#f1f5f9;min-width:24px;text-align:center;font-weight:700;font-size:13px;}
    .dbg-inp{background:#1e293b;border:1px solid #334155;color:#f1f5f9;padding:3px 7px;border-radius:4px;font-family:inherit;font-size:11px;width:150px;}
    .dbg-inp:focus{outline:none;border-color:#3b82f6;}
    .dbg-cb{padding:2px 7px;border-radius:4px;cursor:pointer;border:1px solid;font-family:inherit;font-size:11px;}
    .dbg-cb.on{background:#064e3b;border-color:#34d399;color:#6ee7b7;}
    .dbg-cb.off{background:#1e293b;border-color:#334155;color:#475569;}
    .dbg-sim-banner{background:#422006;border:1px solid #92400e;color:#fcd34d;padding:3px 8px;border-radius:4px;font-size:10px;display:flex;align-items:center;justify-content:space-between;margin:4px 10px 0;}
  `;

  // ── Render ─────────────────────────────────────────────────────────────────
  function v(x) {
    if (x === null || x === undefined) return '<span class="dbg-v n">null</span>';
    if (x === true) return '<span class="dbg-v t">true</span>';
    if (x === false) return '<span class="dbg-v f">false</span>';
    return `<span class="dbg-v">${x}</span>`;
  }

  function renderBody() {
    const c    = window.__abc?.customer  || { loggedIn: false, firstName: null, ordersCount: null, isClub: false, isSubscriber: false, subscriberDiscount: 0, tier: null };
    const cons = window.__abc?.consent   || { preferences: false, analytics: false, marketing: false };
    const sess = window.__abc?.session   || {};
    const b    = getBehavioral();
    const stage    = deriveStage(b.visitCount);
    const stageCfg = STAGES[stage];
    const offer    = deriveOfferFraming(c, b);
    const shelf    = deriveProductShelf(c, b);
    const stageIdx = Object.keys(STAGES).indexOf(stage);
    const isSimulated = !!window.__abc?._simulated;

    const dots = Object.values(STAGES).map((s, i) =>
      `<div class="dbg-dot ${i <= stageIdx ? 'on' : ''}" style="--sc:${stageCfg.color};width:${i === stageIdx ? 20 : 12}px"></div>`
    ).join('');

    const productsDisplay = b.lastProducts.length
      ? b.lastProducts.slice(0,3).join(', ') + (b.lastProducts.length > 3 ? ` +${b.lastProducts.length - 3}` : '')
      : null;

    return `
      ${isSimulated ? `<div class="dbg-sim-banner"><span>⚠ SIMULATED — JS only, server state unchanged</span><button class="dbg-btn blue" id="dbg-actual">↩ actual</button></div>` : ''}

      <div class="dbg-s">
        <div class="dbg-lbl">State</div>
        <div class="dbg-row">
          <div class="dbg-stage" style="background:${stageCfg.bg};color:${stageCfg.color}">${stage} · visit ${b.visitCount || 0}</div>
        </div>
        <div class="dbg-dots">${dots}</div>
        <div class="dbg-row" style="margin-top:4px"><span class="dbg-k">user</span>${v(c.loggedIn ? (c.firstName ? `${c.firstName} (logged in)` : 'logged in') : 'anonymous')}</div>
        <div class="dbg-row"><span class="dbg-k">tier</span>${v(c.tier)}</div>
      </div>

      <div class="dbg-s">
        <div class="dbg-lbl">Customer</div>
        <div class="dbg-row"><span class="dbg-k">loggedIn</span>${v(c.loggedIn)}</div>
        <div class="dbg-row"><span class="dbg-k">ordersCount</span>${v(c.ordersCount)}</div>
        <div class="dbg-row"><span class="dbg-k">isClub</span>${v(c.isClub)}</div>
        <div class="dbg-row"><span class="dbg-k">isSubscriber</span>${v(c.isSubscriber)}</div>
        <div class="dbg-row"><span class="dbg-k">subDiscount</span>${v(c.subscriberDiscount || 0)}</div>
      </div>

      <div class="dbg-s">
        <div class="dbg-lbl">Consent</div>
        <div style="display:flex;gap:4px;flex-wrap:wrap;align-items:center">
          <button class="dbg-cb ${cons.preferences ? 'on' : 'off'}" data-cons="preferences">pref</button>
          <button class="dbg-cb ${cons.analytics ? 'on' : 'off'}" data-cons="analytics">analytics</button>
          <button class="dbg-cb ${cons.marketing ? 'on' : 'off'}" data-cons="marketing">marketing</button>
          <button class="dbg-btn green" id="dbg-consent-all-on" style="margin-left:4px">All on</button>
          <button class="dbg-btn red" id="dbg-reset-consent">All off</button>
        </div>
        <div style="color:#334155;font-size:9px;margin-top:3px">All on/off use real Ketch API · individual toggles are JS-only simulation</div>
      </div>

      <div class="dbg-s">
        <div class="dbg-lbl">Behavioral ${!cons.analytics ? '<span style="color:#f87171;font-size:9px">(analytics consent required)</span>' : ''}</div>
        ${!cons.analytics ? '<div style="color:#475569;font-style:italic;font-size:10px">Toggle analytics above to simulate consent</div>' : `
        <div class="dbg-row"><span class="dbg-k">visitCount</span>${v(b.visitCount || null)}</div>
        <div class="dbg-row"><span class="dbg-k">firstVisit</span>${v(b.firstVisit)}</div>
        <div class="dbg-row"><span class="dbg-k">lastVisit</span>${v(b.lastVisit)}</div>
        <div class="dbg-row"><span class="dbg-k">lastCategory</span>${v(b.lastCategory)}</div>
        <div class="dbg-row"><span class="dbg-k">lastProducts</span>${v(productsDisplay)}</div>
        <div class="dbg-row"><span class="dbg-k">refSource</span>${v(b.refSource)}</div>
        `}
      </div>

      <div class="dbg-s">
        <div class="dbg-lbl">Session</div>
        <div class="dbg-row"><span class="dbg-k">utmSource</span>${v(sess.utmSource || null)}</div>
        <div class="dbg-row"><span class="dbg-k">utmMedium</span>${v(sess.utmMedium || null)}</div>
        <div class="dbg-row"><span class="dbg-k">utmCampaign</span>${v(sess.utmCampaign || null)}</div>
        <div class="dbg-row"><span class="dbg-k">ref</span>${v(sess.ref || null)}</div>
      </div>

      <div class="dbg-s">
        <div class="dbg-lbl">Decisions</div>
        <div class="dbg-card">
          <div class="m">offer → ${offer.match}</div>
          <div class="r">${offer.reason}</div>
          ${offer.cta ? `<div class="c">${offer.cta}</div>` : ''}
        </div>
        <div class="dbg-card">
          <div class="m">shelf → ${shelf.match}</div>
          <div class="r">${shelf.reason}</div>
        </div>
      </div>

      <div class="dbg-s">
        <div class="dbg-lbl">Simulate Visits</div>
        <div style="display:flex;align-items:center;gap:6px;flex-wrap:wrap">
          <div class="dbg-step">
            <button class="dbg-btn" id="dbg-day-back">← day</button>
            <span class="n">${b.visitCount || 0}</span>
            <button class="dbg-btn" id="dbg-day-fwd">day →</button>
          </div>
          <button class="dbg-btn red" id="dbg-reset-visits">Reset</button>
          <button class="dbg-btn red" id="dbg-clear-all">Clear All</button>
        </div>
        <div style="margin-top:5px">
          <input class="dbg-inp" id="dbg-set-visits" type="number" min="0" max="99" placeholder="jump to visit #">
        </div>
      </div>

      <div class="dbg-s">
        <div class="dbg-lbl">Simulate UTM</div>
        <div style="display:flex;gap:4px">
          <input class="dbg-inp" id="dbg-utm-val" placeholder="utm_source" value="${sess.utmSource || ''}">
          <button class="dbg-btn blue" id="dbg-utm-set">Set</button>
          <button class="dbg-btn red" id="dbg-utm-clear">✕</button>
        </div>
      </div>

      <div class="dbg-s">
        <div class="dbg-lbl">Presets <span style="color:#334155;font-size:9px">(JS simulation only)</span></div>
        <div class="dbg-btns">
          <button class="dbg-btn" data-preset="anon-v1">Anon v1</button>
          <button class="dbg-btn" data-preset="anon-v3">Anon v3</button>
          <button class="dbg-btn" data-preset="anon-v5">Anon v5+</button>
          <button class="dbg-btn" data-preset="anon-v7">Anon v7+</button>
        </div>
        <div class="dbg-btns" style="margin-top:2px">
          <button class="dbg-btn" data-preset="loggedin-0">0 orders</button>
          <button class="dbg-btn" data-preset="loggedin-8">8 orders</button>
          <button class="dbg-btn" data-preset="club-no-sns">Club, no S&S</button>
          <button class="dbg-btn" data-preset="club-sns">Club + S&S</button>
        </div>
      </div>
    `;
  }

  // ── Actions ────────────────────────────────────────────────────────────────
  function refresh() {
    const body = document.getElementById('abc-dbg-body');
    if (body) { body.innerHTML = renderBody(); bindBodyEvents(); }
  }

  function simulateVisit(direction) {
    const existing = lsGet(LS.visits) || {};
    const count = Math.max(0, (existing.count || 0) + direction);
    const firstVisit = existing.firstVisit || today();
    const lastVisit = direction > 0
      ? (() => { const d = new Date(existing.lastVisit || today()); d.setDate(d.getDate() + 1); return d.toISOString().slice(0,10); })()
      : existing.lastVisit;
    lsSet(LS.visits, { count, lastVisit, firstVisit });
    refresh();
  }

  function setVisits(n) {
    const existing = lsGet(LS.visits) || {};
    lsSet(LS.visits, { count: n, lastVisit: today(), firstVisit: existing.firstVisit || today() });
    refresh();
  }

  function clearBehavioral() {
    Object.values(LS).forEach(k => localStorage.removeItem(k));
    window.__abc = window.__abc || {};
    window.__abc.behavioral = null;
    refresh();
  }

  function clearSeen() {
    localStorage.removeItem(LS.seen);
    refresh();
  }

  function toggleConsent(key) {
    window.__abc = window.__abc || {};
    window.__abc.consent = window.__abc.consent || {};
    window.__abc.consent[key] = !window.__abc.consent[key];
    document.dispatchEvent(new CustomEvent('abc:consentUpdated', { detail: window.__abc.consent }));
    refresh();
  }

  function setUtm(source) {
    window.__abc = window.__abc || {};
    window.__abc.session = window.__abc.session || {};
    window.__abc.session.utmSource = source;
    refresh();
  }

  function applyPreset(name) {
    const p = PRESETS[name];
    if (!p) return;
    window.__abc = window.__abc || {};
    window.__abc._simulated = name;
    window.__abc._actualCustomer = window.__abc._actualCustomer || window.__abc.customer;
    window.__abc.customer = { ...p.customer };
    if (p.visits) lsSet(LS.visits, { count: p.visits.count, lastVisit: p.visits.lastVisit, firstVisit: p.visits.firstVisit });
    if (p.products) lsSet(LS.products, { ids: p.products, expires: Date.now() + 90 * 864e5 });
    if (p.cat) lsSet(LS.cat, { handle: p.cat, expires: Date.now() + 90 * 864e5 });
    refresh();
  }

  function resetToActual() {
    if (window.__abc?._actualCustomer) {
      window.__abc.customer = window.__abc._actualCustomer;
      delete window.__abc._actualCustomer;
    }
    delete window.__abc._simulated;
    refresh();
  }

  // ── Bind events ────────────────────────────────────────────────────────────
  function bindBodyEvents() {
    const on = (id, fn) => { const el = document.getElementById(id); if (el) el.addEventListener('click', fn); };

    on('dbg-reset-consent', () => {
      // Reject all via Ketch (no page reload), then show the consent banner
      // so the user can go through the real accept flow
      if (typeof window.ketch === 'function') {
        window.ketch('rejectAllConsent', false);
        window.ketch('showExperience', { useAutoInitiated: true });
      } else {
        // Ketch not available — simulate in memory only
        window.__abc = window.__abc || {};
        window.__abc.consent = { preferences: false, analytics: false, marketing: false };
        document.dispatchEvent(new CustomEvent('abc:consentUpdated', { detail: window.__abc.consent }));
        refresh();
      }
    });
    on('dbg-consent-all-on', () => {
      // Accept all via Ketch (no page reload) — triggers real consent flow
      if (typeof window.ketch === 'function') {
        window.ketch('acceptAllConsent', false);
      } else {
        window.__abc = window.__abc || {};
        window.__abc.consent = { preferences: true, analytics: true, marketing: true };
        document.dispatchEvent(new CustomEvent('abc:consentUpdated', { detail: window.__abc.consent }));
        refresh();
      }
    });
    on('dbg-day-back',      () => simulateVisit(-1));
    on('dbg-day-fwd',       () => simulateVisit(1));
    on('dbg-reset-visits',  () => setVisits(0));
    on('dbg-clear-all',     clearBehavioral);
    on('dbg-actual',        resetToActual);
    on('dbg-utm-set',       () => { const el = document.getElementById('dbg-utm-val'); if (el) setUtm(el.value); });
    on('dbg-utm-clear',     () => { setUtm(''); });

    document.getElementById('dbg-set-visits')?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { const n = parseInt(e.target.value, 10); if (!isNaN(n)) setVisits(n); }
    });

    document.querySelectorAll('[data-cons]').forEach(btn => {
      btn.addEventListener('click', () => toggleConsent(btn.dataset.cons));
    });

    document.querySelectorAll('[data-preset]').forEach(btn => {
      btn.addEventListener('click', () => applyPreset(btn.dataset.preset));
    });
  }

  // ── Drag ───────────────────────────────────────────────────────────────────
  function makeDraggable(panel, handle) {
    let ox, oy, startR, startB;
    handle.addEventListener('mousedown', (e) => {
      if (e.target.tagName === 'BUTTON') return;
      ox = e.clientX; oy = e.clientY;
      const rect = panel.getBoundingClientRect();
      startR = window.innerWidth - rect.right;
      startB = window.innerHeight - rect.bottom;
      const move = (e) => {
        panel.style.right = Math.max(0, startR - (e.clientX - ox)) + 'px';
        panel.style.bottom = Math.max(0, startB - (e.clientY - oy)) + 'px';
      };
      const up = () => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up); };
      document.addEventListener('mousemove', move);
      document.addEventListener('mouseup', up);
    });
  }

  // ── Mount ──────────────────────────────────────────────────────────────────
  function mount() {
    const style = document.createElement('style');
    style.textContent = CSS;
    document.head.appendChild(style);

    const panel = document.createElement('div');
    panel.id = 'abc-dbg';
    panel.innerHTML = `
      <div id="abc-dbg-hdr">
        <span class="abc-t">ABC Debug <span>personalization</span></span>
        <button id="dbg-refresh" title="Refresh">↺</button>
        <button id="dbg-collapse" title="Collapse">−</button>
        <button id="dbg-close" title="Close">×</button>
      </div>
      <div id="abc-dbg-body">${renderBody()}</div>
    `;
    document.body.appendChild(panel);

    makeDraggable(panel, document.getElementById('abc-dbg-hdr'));
    bindBodyEvents();

    document.getElementById('dbg-refresh')?.addEventListener('click', refresh);
    document.getElementById('dbg-collapse')?.addEventListener('click', () => {
      panel.classList.toggle('abc-collapsed');
      document.getElementById('dbg-collapse').textContent = panel.classList.contains('abc-collapsed') ? '+' : '−';
    });
    document.getElementById('dbg-close')?.addEventListener('click', () => {
      localStorage.removeItem('abc_debug');
      panel.remove();
    });

    // Auto-refresh when __abc changes via events
    ['abc:consentReady', 'abc:consentUpdated', 'abc:sessionReady', 'abc:behavioralReady', 'abc:personalizeReady'].forEach(evt => {
      document.addEventListener(evt, refresh);
    });
  }

  // ── Console API ────────────────────────────────────────────────────────────
  function exposeAPI() {
    window.__abc = window.__abc || {};
    window.__abc.debug = {
      state() {
        const b = getBehavioral();
        console.group('%c window.__abc', 'color:#60a5fa;font-weight:bold');
        console.log('customer',  { ...window.__abc?.customer });
        console.log('consent',   { ...window.__abc?.consent });
        console.log('session',   { ...window.__abc?.session });
        console.log('behavioral (localStorage)', b);
        console.groupEnd();
      },
      evaluate() {
        const c = window.__abc?.customer || {};
        const b = getBehavioral();
        const stage = deriveStage(b.visitCount);
        const offer = deriveOfferFraming(c, b);
        const shelf = deriveProductShelf(c, b);
        console.group('%c ABC Personalization — evaluate()', 'color:#34d399;font-weight:bold');
        console.log(`%c stage     %c${stage} (visit ${b.visitCount || 0})`, 'color:#475569', 'color:#f1f5f9');
        console.log(`%c user      %c${c.loggedIn ? (c.firstName || 'logged in') + ` · ${c.ordersCount} orders · ${c.tier}` : 'anonymous'}`, 'color:#475569', 'color:#f1f5f9');
        console.group('%c offer framing', 'color:#475569');
        console.log(`match:  ${offer.match}`);
        console.log(`reason: ${offer.reason}`);
        if (offer.cta) console.log(`cta:    ${offer.cta}`);
        console.groupEnd();
        console.group('%c product shelf', 'color:#475569');
        console.log(`match:  ${shelf.match}`);
        console.log(`reason: ${shelf.reason}`);
        console.groupEnd();
        console.groupEnd();
      },
      incrementVisit()   { simulateVisit(1); },
      decrementVisit()   { simulateVisit(-1); },
      setVisits(n)       { setVisits(n); },
      clearBehavioral()  { clearBehavioral(); },
      setConsent(obj)    { window.__abc.consent = { ...window.__abc.consent, ...obj }; document.dispatchEvent(new CustomEvent('abc:consentUpdated')); refresh(); },
      setUtm(source, medium = '', campaign = '') {
        window.__abc.session = { ...window.__abc.session, utmSource: source, utmMedium: medium, utmCampaign: campaign };
        refresh();
      },
      preset(name)       { applyPreset(name); },
      resetToActual()    { resetToActual(); },
      panel()            { document.getElementById('abc-dbg')?.classList.toggle('abc-collapsed'); },
      help() {
        console.group('%c __abc.debug — available commands', 'color:#60a5fa;font-weight:bold');
        [
          ['state()',                         'pretty-print full window.__abc state'],
          ['evaluate()',                      'show offer + shelf decisions with reasoning'],
          ['incrementVisit()',                'simulate next day (+1 visit)'],
          ['decrementVisit()',                'undo a day'],
          ['setVisits(n)',                    'jump to visit count n'],
          ['clearBehavioral()',               'wipe all abc_* localStorage keys'],
          ['setConsent({preferences:true})',  'override consent flags'],
          ['setUtm("instagram","social","")', 'inject UTM into session'],
          ['preset("anon-v3")',               'load a preset state (JS simulation only)'],
          ['resetToActual()',                 'restore Liquid-emitted customer state'],
          ['panel()',                         'toggle panel visibility'],
        ].forEach(([cmd, desc]) => console.log(`%c ${cmd.padEnd(35)} %c${desc}`, 'color:#f1f5f9', 'color:#64748b'));
        console.log('\n%cPreset names: anon-v1, anon-v3, anon-v5, anon-v7, loggedin-0, loggedin-8, club-no-sns, club-sns', 'color:#64748b');
        console.groupEnd();
      },
    };
    console.log('%c ABC Debug active %c — run __abc.debug.help() for commands', 'background:#1e3a5f;color:#7dd3fc;padding:2px 6px;border-radius:3px', 'color:#475569');
  }

  // ── Init ───────────────────────────────────────────────────────────────────
  exposeAPI();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount);
  } else {
    mount();
  }

})();
